import { useMutation, useQuery } from "@apollo/client";
import { Banner, Button, Toast } from "@aurem/ui";
import { useActiveProductAssignment } from "hooks/employee";
import { useNavigation } from "hooks/navigate";
import { useAuth } from "providers/AuthProvider";
import { useChatwoot } from "providers/ChatwootProvider";
import { useEmployment } from "providers/employee/EmploymentProvider";
import { useI18n } from "providers/I18nProvider";
import type { PropsWithChildren, ReactElement } from "react";
import { createContext, useContext } from "react";

import {
  gql,
  KycRequestSource,
  KycRequestStatus,
  UserKycRequestQuery,
} from "types";

const CREATE_KYC_REQUEST = gql(/* GraphQL */ `
  mutation CreateKycRequest(
    $employeeId: String!
    $requestedVia: KycRequestSource
  ) {
    createKycRequest(employeeId: $employeeId, requestedVia: $requestedVia) {
      id
    }
  }
`);

const USER_KYC_REQUEST = gql(/* GraphQL */ `
  query UserKycRequest {
    user {
      id
      findEmployment {
        id
        firstName
        lastName
      }
      kycRequest {
        id
        status
        note
        expiredAt
      }
    }
  }
`);

export type KycButtonWrapperProps = PropsWithChildren<{
  note?: string;
  requestedVia?: KycRequestSource;
}>;

export type KycContextType = {
  isKycVerified: boolean;
  kycRequest: UserKycRequestQuery["user"]["kycRequest"];
  KycButtonWrapper: (props: KycButtonWrapperProps) => ReactElement | null;
  KycBanner: () => ReactElement | null;
};

export const KycContext = createContext<KycContextType | undefined>(undefined);

export const KycVerificationProvider = ({ children }: PropsWithChildren) => {
  const { authState } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigation();
  const { show } = useChatwoot();
  const { employmentId } = useEmployment();
  const { activeAssignmentId } = useActiveProductAssignment();
  const [createKycRequest] = useMutation(CREATE_KYC_REQUEST);
  const { data } = useQuery(USER_KYC_REQUEST, {
    skip: !authState?.accessToken,
  });
  const employeeId = data?.user.findEmployment?.id ?? "";
  const kycRequest = data?.user.kycRequest;
  const isKycVerified = kycRequest?.status === KycRequestStatus.Approved;

  const getKycRoute = (kycRequestId: string) => {
    if (employmentId && activeAssignmentId) {
      return `/employment/${employmentId}/${activeAssignmentId}/kyc-request/${kycRequestId}`;
    }

    return `/kyc-request/${kycRequestId}`;
  };

  const navigateToKyc = () => {
    const kycRequestId = data?.user.kycRequest?.id;
    if (kycRequestId) {
      navigate(getKycRoute(kycRequestId));
    }
  };

  const createKyc = async (requestedVia?: KycRequestSource) => {
    try {
      const { data } = await createKycRequest({
        variables: { employeeId, requestedVia },
        refetchQueries: [USER_KYC_REQUEST],
      });
      const kycRequestId = data?.createKycRequest.id;
      if (kycRequestId) {
        navigate(getKycRoute(kycRequestId));
      }
    } catch (error) {
      Toast.warning({
        title: "There was an issue creating the new KYC request.",
      });
    }
  };

  const KycButtonWrapper = ({
    children,
    note,
    requestedVia,
  }: KycButtonWrapperProps) => {
    if (isKycVerified) {
      return <>{children}</>;
    }

    const bannerText =
      note ?? t("apps.employee.components.verificationBanner.note");

    const requiresUserAction =
      kycRequest &&
      [
        KycRequestStatus.DocumentsRequired,
        KycRequestStatus.ResubmissionRequired,
        KycRequestStatus.AdditionalDocumentRequired,
        KycRequestStatus.Rejected,
      ].includes(kycRequest.status);

    const isUnderReview =
      kycRequest &&
      [KycRequestStatus.DocumentsInReview, KycRequestStatus.OnHold].includes(
        kycRequest.status,
      );

    if (!kycRequest || kycRequest.status === KycRequestStatus.Cancelled) {
      return (
        <div className="space-y-4">
          <Banner text={bannerText} />
          <Button
            className="w-full"
            onClick={() => createKyc(requestedVia)}
            // TODO: Connect citizenship confirmation check when available
            // disabled={!isCitizenshipUpToDate()}
          >
            {t("apps.employee.components.verificationBanner.cta.procced")}
          </Button>
        </div>
      );
    }

    if (isUnderReview) {
      return (
        <div className="space-y-4">
          <Banner text={bannerText} />
          <Button className="w-full" disabled>
            {t("apps.employee.components.verificationBanner.cta.pending")}
          </Button>
        </div>
      );
    }

    if (requiresUserAction) {
      return (
        <div className="space-y-4">
          <Banner text={bannerText} />
          <Button className="w-full" onClick={navigateToKyc}>
            {t("apps.employee.components.verificationBanner.cta.procced")}
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Banner text={bannerText} />
      </div>
    );
  };

  const KycBanner = () => {
    switch (kycRequest?.status) {
      case KycRequestStatus.DocumentsInReview:
      case KycRequestStatus.OnHold:
        return (
          <Banner
            variant="bleed"
            color="warning"
            title={t(
              "apps.employee.components.verificationBanner.pending.title",
            )}
            text={t(
              "apps.employee.components.verificationBanner.pending.description",
            )}
          />
        );
      case KycRequestStatus.ResubmissionRequired:
        return (
          <Banner
            variant="bleed"
            color="warning"
            title={t("apps.employee.components.verificationBanner.retry.title")}
            text={t(
              "apps.employee.components.verificationBanner.retry.description",
            )}
            actions={[
              {
                children: t(
                  "apps.employee.components.verificationBanner.cta.resubmit",
                ),
                onClick: navigateToKyc,
              },
            ]}
          />
        );
      case KycRequestStatus.Rejected:
        return (
          <Banner
            variant="bleed"
            color="danger"
            title={t(
              "apps.employee.components.verificationBanner.rejected.title",
            )}
            text={t(
              "apps.employee.components.verificationBanner.rejected.description",
            )}
            actions={[
              {
                children: t("apps.employee.common.contactSupport"),
                onClick: () => show(),
              },
            ]}
          />
        );

      default:
        return null;
    }
  };

  return (
    <KycContext.Provider
      value={{
        isKycVerified,
        kycRequest,
        KycButtonWrapper,
        KycBanner,
      }}
    >
      {children}
    </KycContext.Provider>
  );
};

export const useKycVerification = () => {
  const context = useContext(KycContext);

  if (!context) {
    throw new Error(
      "useKycVerification must be used within a KycVerificationrProvider",
    );
  }

  return context;
};
